import { Button, FormField } from '@chopchop-se/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { setLanguage } from '../../i18n';
import { errorMessage } from '../../lib/notify';
import { devLogin, isDevLoginAvailable } from '../auth-api';
import { homePathFor, useAuthStore } from '../auth-store';
import { AuthCard } from './AuthCard';
import { FullPageSpinner } from '../../components/FullPageSpinner';

/**
 * `/dev-login` — the E2E / local-development bypass (api: `GET
 * /api/auth/dev-login?email=…`, refused unless `DEV_AUTH_BYPASS=true` and
 * NODE_ENV≠production).
 *
 * The route always exists in the bundle; whether it WORKS is decided by the
 * server. Availability is probed (a bare call returns 404 when the bypass is
 * off, 400 when it is on — no session is issued either way), so this page shows
 * an honest "not enabled" message in production instead of a form that 404s.
 *
 * Seeded users (apps/api/src/db/seed/dev-users.seed.ts) are listed as one-click
 * buttons because that is what an E2E run and a new developer both need first.
 */
const SEEDED_USERS = [
  'dev-super-admin@chopchop.se',
  'dev-admin@chopchop.se',
  'dev-lager@chopchop.se',
  'dev-arbetsledare@chopchop.se',
  'dev-produktion@chopchop.se',
  'dev-ekonomi@chopchop.se',
  'dev-bestallare@chopchop.se',
] as const;

export function DevLoginPage(): ReactNode {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState<string>(SEEDED_USERS[2]);

  const available = useQuery({
    queryKey: ['auth', 'dev-login-available'],
    queryFn: ({ signal }) => isDevLoginAvailable(signal),
    staleTime: Infinity,
    retry: false,
  });

  const signIn = useMutation({
    mutationFn: (target: string) => devLogin(target),
    onSuccess: (me) => {
      setSession(me);
      setLanguage(me.user.language);
      void navigate(homePathFor(me.user), { replace: true });
    },
  });

  if (available.isPending) return <FullPageSpinner />;

  if (!available.data) {
    return (
      <AuthCard
        title={t('auth.devLoginTitle')}
        subtitle={t('auth.devLoginUnavailable')}
        footer={
          <Link to="/login" className="underline underline-offset-4 hover:text-ink">
            {t('auth.signInWithEms')}
          </Link>
        }
      >
        <div />
      </AuthCard>
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    signIn.mutate(email);
  }

  return (
    <AuthCard
      title={t('auth.devLoginTitle')}
      subtitle={t('auth.devLoginDescription')}
      footer={
        <Link to="/login" className="underline underline-offset-4 hover:text-ink">
          {t('auth.signInWithEms')}
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <FormField
          label={t('auth.devLoginEmail')}
          type="email"
          name="email"
          autoComplete="username"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          error={signIn.isError ? errorMessage(signIn.error) : undefined}
        />

        <Button type="submit" className="w-full" loading={signIn.isPending}>
          {t('auth.devLogin')}
        </Button>

        <div className="flex flex-col gap-1.5 border-t border-surface-border pt-4">
          {SEEDED_USERS.map((seeded) => (
            <button
              key={seeded}
              type="button"
              onClick={() => {
                setEmail(seeded);
                signIn.mutate(seeded);
              }}
              className="rounded-lg px-2 py-1.5 text-left font-mono text-xs text-ink-light transition-colors hover:bg-brand-600/[0.06] hover:text-ink"
            >
              {seeded}
            </button>
          ))}
        </div>
      </form>
    </AuthCard>
  );
}
